import { useCallback, useEffect, useRef } from "react";

/**
 * ChainFlow OS · useHoldRepeat
 * ────────────────────────────────────────────────────────────
 * The foundational operational numeric interaction primitive.
 *
 * Every numeric stepper across the platform — Mission Control bulk
 * controls, Director Centre range filters, forecasting configuration,
 * revenue thresholds, operational policy settings, and the Pipeline
 * Exposure Audit — must consume this hook so that timing,
 * acceleration, touch parity, and bound handling stay identical and
 * unmistakably ChainFlow.
 *
 * The hook is intentionally UI-agnostic. It returns a `getBindings(dir)`
 * helper that produces a complete set of Pointer Event listeners and
 * `touch-action` styling to spread onto any +/- button. Consumers own
 * the visual treatment; the hook owns the interaction grammar.
 *
 * Locked v1 timing (do not tune per-consumer):
 *  • settle delay     : 380 ms before continuous repeat begins
 *  • initial interval : 140 ms, decays toward 38 ms (4 ms/tick)
 *  • step multiplier  : 1× → 2× (after 12 ticks)
 *                       → 4× (after 24 ticks) → 6× (after 40 ticks)
 *  • stop triggers    : pointer up · cancel · leave · blur · unmount
 *  • bound stop       : auto-stops when min/max reached
 *
 * @param {Object} opts
 * @param {() => number} opts.getValue   Live value reader (use refs).
 * @param {(next:number) => void} opts.onChange  Setter; receives the
 *                                                clamped next value on
 *                                                every immediate press
 *                                                and every repeat tick.
 * @param {number} opts.lo               Minimum bound (inclusive).
 * @param {number} opts.hi               Maximum bound (inclusive).
 * @param {number} [opts.step=1]         Base step amount.
 *
 * @returns {{
 *   getBindings: (dir: -1 | 1) => Object,
 *   stop: () => void,
 *   isHolding: () => boolean,
 * }}
 */
export function useHoldRepeat({ getValue, onChange, lo, hi, step = 1 }) {
  // Mutable interaction state — refs so async timers always see truth.
  const repeatRef = useRef({ timer: null, dir: 0 });
  const optsRef = useRef({ getValue, onChange, lo, hi, step });
  optsRef.current = { getValue, onChange, lo, hi, step };

  const clamp = (v) => {
    const { lo: l, hi: h } = optsRef.current;
    return Math.max(l, Math.min(h, v));
  };

  const stop = useCallback(() => {
    const r = repeatRef.current;
    if (r.timer) { clearTimeout(r.timer); r.timer = null; }
    r.dir = 0;
  }, []);

  const tick = useCallback((dir, ticks) => {
    const r = repeatRef.current;
    if (r.dir !== dir) return;
    const { getValue: get, onChange: set, step: base } = optsRef.current;
    const mult = ticks > 40 ? 6 : ticks > 24 ? 4 : ticks > 12 ? 2 : 1;
    const cur = get();
    const next = clamp(cur + dir * base * mult);
    if (next === cur) { stop(); return; }
    set(next);
    const interval = Math.max(38, 140 - ticks * 4);
    r.timer = setTimeout(() => tick(dir, ticks + 1), interval);
  }, [stop]);

  const start = useCallback((dir, e) => {
    if (e && e.preventDefault) e.preventDefault();
    const { getValue: get, onChange: set, step: base } = optsRef.current;
    const cur = get();
    const first = clamp(cur + dir * base);
    if (first !== cur) set(first);
    repeatRef.current.dir = dir;
    repeatRef.current.timer = setTimeout(() => tick(dir, 1), 380);
  }, [tick]);

  useEffect(() => stop, [stop]);

  const getBindings = useCallback((dir) => ({
    onPointerDown: (e) => start(dir, e),
    onPointerUp: stop,
    onPointerCancel: stop,
    onPointerLeave: stop,
    onBlur: stop,
    onContextMenu: (e) => e.preventDefault(),
    style: { touchAction: "manipulation" },
  }), [start, stop]);

  return {
    getBindings,
    stop,
    isHolding: () => repeatRef.current.dir !== 0,
  };
}

export default useHoldRepeat;
